import React, { useState, useEffect, useCallback, useMemo, } from 'react';
import { createPortal } from 'react-dom';
import {
  Terminal,
  AlertTriangle,
  AlertCircle,
  Info,
  Search,
  X,
  Copy,
  Check,
  RefreshCw,
  Download,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ChevronLeft,
  Clock,
  Filter,
  FileText,
  Loader2,
  ShieldAlert,
  BarChart2,
  HelpCircle,
  Hash,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import baseapi from '../../../services/baseapi';
import { showError, showSuccess, showInfo } from '../../../utils/toast';

export interface OracleAlertLogEntry {
  agent_id?: number;
  asset_id?: number;
  client_id?: number;
  cluster_id?: string;
  collected_at?: string;
  config_id?: string;
  db_type?: string;
  hostname?: string;
  indexed_at?: string;
  ip?: string;
  log_timestamp?: string;
  master_agent_id?: string;
  message_text: string;
  message_type?: number;
  organization_id?: number;
  os?: string;
  severity: string;
  timestamp: string;
}

export interface OracleAlertLogByHour {
  hour: string;
  error: number;
  warning: number;
  info: number;
}

export interface OracleAlertLogSummary {
  total: number;
  error: number;
  warning: number;
  info: number;
  unknown?: number;
}

interface OracleAlertLogResponse {
  status: boolean;
  applicable?: boolean;
  fresh?: boolean;
  collected_at?: string;
  data: {
    summary: OracleAlertLogSummary;
    by_hour?: OracleAlertLogByHour[];
    entries: OracleAlertLogEntry[];
  };
}

interface OracleAlertLogViewProps {
  activeCluster: string;
  clusterName?: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

const formatDate = (isoString?: string): string => {
  if (!isoString) return '—';
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return isoString;
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  } catch {
    return isoString;
  }
};

const formatTimeAgo = (isoString?: string): string => {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    if (diffMs < 0) return 'just now';
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  } catch {
    return '';
  }
};

const formatHourLabel = (isoString: string): string => {
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return isoString;
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch {
    return isoString;
  }
};

export const OracleAlertLogView: React.FC<OracleAlertLogViewProps> = ({
  activeCluster,
  clusterName,
  clientId,
  refreshKey = 0,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [data, setData] = useState<OracleAlertLogResponse['data'] | null>(null);
  const [meta, setMeta] = useState<{ applicable?: boolean; fresh?: boolean; collected_at?: string }>({});

  // Filtering & Search
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedOraCode, setSelectedOraCode] = useState<string>('all');
  const [showChart, setShowChart] = useState<boolean>(true);

  // Pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);

  // Expanded rows for multi-line logs
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  // Selected entry for drawer detail
  const [selectedEntry, setSelectedEntry] = useState<OracleAlertLogEntry | null>(null);

  // Copy states
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [copiedAll, setCopiedAll] = useState<boolean>(false);

  // Auto-refresh interval (0 = off, 15, 30, 60 seconds)
  const [autoRefreshInterval, setAutoRefreshInterval] = useState<number>(0);

  // Fetch Oracle alert log
  const fetchAlertLogs = useCallback(
    async (isManualRefresh = false) => {
      if (!activeCluster) return;
      if (isManualRefresh) setRefreshing(true);
      else setLoading(true);

      try {
        const params: any = {
          cluster_id: activeCluster,
        };
        if (clientId) {
          params.client_id = clientId;
        }

        const res = await baseapi.get('/db_monitor_dashboard/oracle/alert_log', { params });
        const resData: OracleAlertLogResponse = res.data;

        if (resData && resData.status && resData.data) {
          setData(resData.data);
          setMeta({
            applicable: resData.applicable,
            fresh: resData.fresh,
            collected_at: resData.collected_at,
          });
        } else {
          setData(null);
        }
      } catch (err: any) {
        console.error('Failed to fetch Oracle alert logs:', err);
        showError(err.response?.data?.message || err.message || 'Failed to load Oracle alert log');
        setData(null);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [activeCluster, clientId]
  );

  useEffect(() => {
    fetchAlertLogs();
  }, [fetchAlertLogs, refreshKey]);

  // Auto-refresh handler
  useEffect(() => {
    if (autoRefreshInterval <= 0) return;
    const interval = setInterval(() => {
      fetchAlertLogs(true);
    }, autoRefreshInterval * 1000);
    return () => clearInterval(interval);
  }, [autoRefreshInterval, fetchAlertLogs]);

  // Extract all unique ORA-XXXX error codes across entries
  const detectedOraCodes = useMemo(() => {
    if (!data?.entries) return [];
    const set = new Set<string>();
    const oraRegex = /ORA-\d{4,5}/g;
    data.entries.forEach((e) => {
      if (e.message_text) {
        const matches = e.message_text.match(oraRegex);
        if (matches) {
          matches.forEach((m) => set.add(m));
        }
      }
    });
    return Array.from(set).sort();
  }, [data?.entries]);

  // Extract all unique message types
  const detectedMessageTypes = useMemo(() => {
    if (!data?.entries) return [];
    const set = new Set<number>();
    data.entries.forEach((e) => {
      if (e.message_type !== undefined && e.message_type !== null) {
        set.add(e.message_type);
      }
    });
    return Array.from(set).sort((a, b) => a - b);
  }, [data?.entries]);

  // Calculated Summary (including dynamic count of unknown severity)
  const summaryCounts = useMemo(() => {
    if (!data?.entries) {
      return {
        total: data?.summary?.total || 0,
        error: data?.summary?.error || 0,
        warning: data?.summary?.warning || 0,
        info: data?.summary?.info || 0,
        unknown: 0,
      };
    }

    let error = 0;
    let warning = 0;
    let info = 0;
    let unknown = 0;

    data.entries.forEach((e) => {
      const sev = (e.severity || 'unknown').toLowerCase();
      if (sev === 'error') error++;
      else if (sev === 'warning' || sev === 'warn') warning++;
      else if (sev === 'info' || sev === 'information') info++;
      else unknown++;
    });

    return {
      total: data.entries.length,
      error: data.summary?.error ?? error,
      warning: data.summary?.warning ?? warning,
      info: data.summary?.info ?? info,
      unknown,
    };
  }, [data]);

  // Filtered entries
  const filteredEntries = useMemo(() => {
    if (!data?.entries) return [];

    return data.entries.filter((entry) => {
      // Severity filter
      if (selectedSeverity !== 'all') {
        const entrySev = (entry.severity || 'unknown').toLowerCase();
        if (selectedSeverity === 'unknown' && entrySev !== 'unknown') return false;
        if (selectedSeverity !== 'unknown' && entrySev !== selectedSeverity) return false;
      }

      // Message Type filter
      if (selectedType !== 'all') {
        if (String(entry.message_type) !== selectedType) return false;
      }

      // ORA Code filter
      if (selectedOraCode !== 'all') {
        if (!entry.message_text || !entry.message_text.includes(selectedOraCode)) return false;
      }

      // Text query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const inMessage = entry.message_text?.toLowerCase().includes(q);
        const inHost = entry.hostname?.toLowerCase().includes(q);
        const inIp = entry.ip?.toLowerCase().includes(q);
        const inConfig = entry.config_id?.toLowerCase().includes(q);
        const inSeverity = entry.severity?.toLowerCase().includes(q);
        const inType = String(entry.message_type || '').includes(q);
        if (!inMessage && !inHost && !inIp && !inConfig && !inSeverity && !inType) return false;
      }

      return true;
    });
  }, [data?.entries, selectedSeverity, selectedType, selectedOraCode, searchQuery]);

  // Pagination calculation
  const totalEntries = filteredEntries.length;
  const totalPages = Math.ceil(totalEntries / pageSize) || 1;
  const paginatedEntries = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredEntries.slice(start, start + pageSize);
  }, [filteredEntries, currentPage, pageSize]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
    setExpandedRows({});
  }, [searchQuery, selectedSeverity, selectedType, selectedOraCode, pageSize]);

  // Hourly chart data formatted
  const chartData = useMemo(() => {
    if (!data?.by_hour || data.by_hour.length === 0) return [];
    return data.by_hour.map((item) => ({
      ...item,
      label: formatHourLabel(item.hour),
      fullHour: formatDate(item.hour),
    }));
  }, [data?.by_hour]);

  // Helper to copy single log message
  const handleCopyMessage = (text: string, index: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    showSuccess('Log message copied to clipboard');
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  // Helper to copy all filtered logs
  const handleCopyAll = () => {
    if (filteredEntries.length === 0) {
      showInfo('No logs to copy');
      return;
    }
    const logDump = filteredEntries
      .map(
        (e) =>
          `[${formatDate(e.timestamp)}] [${(e.severity || 'UNKNOWN').toUpperCase()}] (Type ${e.message_type ?? 'N/A'})\n${e.message_text}`
      )
      .join('\n----------------------------------------\n');

    navigator.clipboard.writeText(logDump);
    setCopiedAll(true);
    showSuccess(`Copied ${filteredEntries.length} log entries`);
    setTimeout(() => setCopiedAll(false), 2000);
  };

  // Export to JSON
  const handleExportJSON = () => {
    if (filteredEntries.length === 0) {
      showInfo('No logs to export');
      return;
    }
    const jsonStr = JSON.stringify(filteredEntries, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `oracle_alert_logs_${activeCluster}_${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);
    showSuccess('Logs exported as JSON');
  };

  // Render severity badge
  const renderSeverityBadge = (severity?: string) => {
    const s = (severity || 'unknown').toLowerCase();
    if (s === 'error' || s === 'err') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800/60">
          <AlertCircle className="w-3 h-3 text-rose-500" />
          ERROR
        </span>
      );
    }
    if (s === 'warning' || s === 'warn') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800/60">
          <AlertTriangle className="w-3 h-3 text-amber-500" />
          WARN
        </span>
      );
    }
    if (s === 'info' || s === 'information') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/60">
          <Info className="w-3 h-3 text-blue-500" />
          INFO
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-gray-100 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
        <HelpCircle className="w-3 h-3 text-gray-400" />
        {severity?.toUpperCase() || 'UNKNOWN'}
      </span>
    );
  };

  // Format message text with highlighted ORA codes
  const renderFormattedMessageText = (text: string) => {
    if (!text) return '';
    const oraRegex = /(ORA-\d{4,5})/g;
    const parts = text.split(oraRegex);

    return parts.map((part, i) => {
      if (oraRegex.test(part)) {
        return (
          <span
            key={i}
            className="inline-block px-1 py-0.2 mx-0.5 rounded bg-rose-500/15 dark:bg-rose-500/25 text-rose-600 dark:text-rose-400 font-bold border border-rose-300/40 dark:border-rose-700/60"
          >
            {part}
          </span>
        );
      }
      return <span key={i}>{part}</span>;
    });
  };

  return (
    <div className="space-y-4 pb-12 font-sans">
      {/* Top Header Card */}
      <div className="bg-white dark:bg-gray-900/90 rounded-xl border border-gray-200 dark:border-gray-800/80 p-4 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-orange-500/10 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400 border border-orange-500/20">
              <Terminal className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-base font-bold text-gray-900 dark:text-white">
                  Log Monitor
                </h1>
                <span className="px-2 py-0.5 text-[10px] font-semibold tracking-wider uppercase rounded-full bg-orange-100 dark:bg-orange-950/60 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-800">
                  ORACLE
                </span>
                {meta.fresh && (
                  <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    LIVE
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Target Database Cluster: <span className="font-semibold text-gray-800 dark:text-gray-200">{clusterName || activeCluster}</span>
                {meta.collected_at && (
                  <span className="ml-2 pl-2 border-l border-gray-200 dark:border-gray-700">
                    Last Collected: {formatDate(meta.collected_at)} ({formatTimeAgo(meta.collected_at)})
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Action Toolbar */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Auto-Refresh Select */}
            <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700">
              <Clock className="w-3.5 h-3.5 text-gray-400" />
              <span>Auto-refresh:</span>
              <select
                value={autoRefreshInterval}
                onChange={(e) => setAutoRefreshInterval(Number(e.target.value))}
                className="bg-transparent border-0 text-xs font-semibold text-gray-800 dark:text-gray-200 focus:ring-0 cursor-pointer p-0"
              >
                <option value={0} className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">Off</option>
                <option value={15} className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">15s</option>
                <option value={30} className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">30s</option>
                <option value={60} className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">60s</option>
              </select>
            </div>

            {/* Copy All Button */}
            <button
              onClick={handleCopyAll}
              disabled={loading || filteredEntries.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 cursor-pointer shadow-sm"
              title="Copy visible logs to clipboard"
            >
              {copiedAll ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
              <span>{copiedAll ? 'Copied' : 'Copy Logs'}</span>
            </button>

            {/* Export JSON Button */}
            <button
              onClick={handleExportJSON}
              disabled={loading || filteredEntries.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 cursor-pointer shadow-sm"
              title="Export filtered logs as JSON file"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Export</span>
            </button>

            {/* Refresh Button */}
            <button
              onClick={() => fetchAlertLogs(true)}
              disabled={loading || refreshing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-orange-600 hover:bg-orange-500 text-white transition-colors disabled:opacity-50 cursor-pointer shadow-sm"
              title="Refresh Alert Log"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* KPI Cards: Total, Error, Warning, Info, Unknown */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {/* Total */}
        <button
          onClick={() => setSelectedSeverity('all')}
          className={`p-3.5 rounded-xl border transition-all text-left cursor-pointer shadow-sm ${selectedSeverity === 'all'
              ? 'bg-orange-50/70 dark:bg-orange-950/20 border-orange-500/50 shadow-orange-500/10 ring-2 ring-orange-500/20'
              : 'bg-white dark:bg-gray-900/80 border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
            }`}
        >
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Total Entries
            </span>
            <FileText className="w-4 h-4 text-orange-500" />
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono">
            {summaryCounts.total.toLocaleString()}
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
            All alert log records
          </div>
        </button>

        {/* Errors */}
        <button
          onClick={() => setSelectedSeverity('error')}
          className={`p-3.5 rounded-xl border transition-all text-left cursor-pointer shadow-sm ${selectedSeverity === 'error'
              ? 'bg-rose-50/70 dark:bg-rose-950/30 border-rose-500/50 shadow-rose-500/10 ring-2 ring-rose-500/20'
              : 'bg-white dark:bg-gray-900/80 border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
            }`}
        >
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-rose-600 dark:text-rose-400">
              Errors
            </span>
            <AlertCircle className="w-4 h-4 text-rose-500" />
          </div>
          <div className="text-xl font-bold text-rose-600 dark:text-rose-400 font-mono">
            {summaryCounts.error.toLocaleString()}
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
            Critical ORA errors & failures
          </div>
        </button>

        {/* Warnings */}
        <button
          onClick={() => setSelectedSeverity('warning')}
          className={`p-3.5 rounded-xl border transition-all text-left cursor-pointer shadow-sm ${selectedSeverity === 'warning'
              ? 'bg-amber-50/70 dark:bg-amber-950/30 border-amber-500/50 shadow-amber-500/10 ring-2 ring-amber-500/20'
              : 'bg-white dark:bg-gray-900/80 border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
            }`}
        >
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400">
              Warnings
            </span>
            <AlertTriangle className="w-4 h-4 text-amber-500" />
          </div>
          <div className="text-xl font-bold text-amber-600 dark:text-amber-400 font-mono">
            {summaryCounts.warning.toLocaleString()}
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
            Cautionary alerts
          </div>
        </button>

        {/* Info */}
        <button
          onClick={() => setSelectedSeverity('info')}
          className={`p-3.5 rounded-xl border transition-all text-left cursor-pointer shadow-sm ${selectedSeverity === 'info'
              ? 'bg-blue-50/70 dark:bg-blue-950/30 border-blue-500/50 shadow-blue-500/10 ring-2 ring-blue-500/20'
              : 'bg-white dark:bg-gray-900/80 border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
            }`}
        >
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
              Info
            </span>
            <Info className="w-4 h-4 text-blue-500" />
          </div>
          <div className="text-xl font-bold text-blue-600 dark:text-blue-400 font-mono">
            {summaryCounts.info.toLocaleString()}
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
            Routine operations & notices
          </div>
        </button>

        {/* Diagnostics / Unknown */}
        <button
          onClick={() => setSelectedSeverity('unknown')}
          className={`p-3.5 rounded-xl border transition-all text-left cursor-pointer shadow-sm ${selectedSeverity === 'unknown'
              ? 'bg-purple-50/70 dark:bg-purple-950/30 border-purple-500/50 shadow-purple-500/10 ring-2 ring-purple-500/20'
              : 'bg-white dark:bg-gray-900/80 border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
            }`}
        >
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-purple-600 dark:text-purple-400">
              Diagnostics
            </span>
            <HelpCircle className="w-4 h-4 text-purple-500" />
          </div>
          <div className="text-xl font-bold text-purple-600 dark:text-purple-400 font-mono">
            {summaryCounts.unknown.toLocaleString()}
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
            Trace dumps & OS reports
          </div>
        </button>
      </div>

      {/* Hourly Trend Activity Chart (Collapsible) */}
      {chartData.length > 0 && (
        <div className="bg-white dark:bg-gray-900/90 rounded-xl border border-gray-200 dark:border-gray-800/80 p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <BarChart2 className="w-4 h-4 text-orange-500" />
              <h2 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
                Alert Frequency by Hour
              </h2>
              <span className="text-[11px] text-gray-500 dark:text-gray-400 font-normal">
                (Distribution of errors, warnings, and informational logs)
              </span>
            </div>
            <button
              onClick={() => setShowChart(!showChart)}
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex items-center gap-1 cursor-pointer"
            >
              <span>{showChart ? 'Hide Chart' : 'Show Chart'}</span>
              {showChart ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          </div>

          {showChart && (
            <div className="h-44 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#374151" opacity={0.2} />
                  <XAxis
                    dataKey="label"
                    stroke="#9ca3af"
                    fontSize={11}
                    tickLine={false}
                    axisLine={{ stroke: '#4b5563', opacity: 0.3 }}
                  />
                  <YAxis
                    stroke="#9ca3af"
                    fontSize={11}
                    tickLine={false}
                    axisLine={{ stroke: '#4b5563', opacity: 0.3 }}
                    allowDecimals={false}
                  />
                  <Tooltip
                    content={({ active, payload, }) => {
                      if (active && payload && payload.length) {
                        const item = payload[0].payload;
                        return (
                          <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-lg p-2.5 shadow-xl text-[11px] min-w-[170px]">
                            <div className="font-bold text-gray-800 dark:text-gray-200 border-b border-gray-100 dark:border-gray-800 pb-1 mb-1.5 flex items-center gap-1.5">
                              <Clock className="w-3 h-3 text-orange-500" />
                              <span>{item.fullHour}</span>
                            </div>
                            <div className="space-y-1">
                              <div className="flex justify-between items-center text-rose-600 dark:text-rose-400">
                                <span>Errors:</span>
                                <span className="font-mono font-bold">{item.error}</span>
                              </div>
                              <div className="flex justify-between items-center text-amber-600 dark:text-amber-400">
                                <span>Warnings:</span>
                                <span className="font-mono font-bold">{item.warning}</span>
                              </div>
                              <div className="flex justify-between items-center text-blue-600 dark:text-blue-400">
                                <span>Info:</span>
                                <span className="font-mono font-bold">{item.info}</span>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="error" name="Errors" stackId="a" fill="#f43f5e" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="warning" name="Warnings" stackId="a" fill="#f59e0b" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="info" name="Info" stackId="a" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Filter Toolbar & Search */}
      <div className="bg-white dark:bg-gray-900/90 rounded-xl border border-gray-200 dark:border-gray-800/80 p-3.5 shadow-sm space-y-3">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          {/* Search Box */}
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search alert log messages, ORA errors, file names, hostnames..."
              className="w-full pl-9 pr-8 py-2 text-xs rounded-lg bg-gray-50 dark:bg-gray-800/70 border border-gray-200 dark:border-gray-700/80 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-orange-500"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Dropdown Filters */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Severity Filter */}
            <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-xs">
              <Filter className="w-3 h-3 text-gray-400" />
              <span className="text-gray-500 dark:text-gray-400 text-[11px]">Severity:</span>
              <select
                value={selectedSeverity}
                onChange={(e) => setSelectedSeverity(e.target.value)}
                className="bg-transparent border-0 text-xs font-semibold text-gray-800 dark:text-gray-200 focus:ring-0 cursor-pointer p-0"
              >
                <option value="all" className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">All Severities</option>
                <option value="error" className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">Error</option>
                <option value="warning" className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">Warning</option>
                <option value="info" className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">Info</option>
                <option value="unknown" className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">Diagnostics / Other</option>
              </select>
            </div>

            {/* Message Type Filter */}
            {detectedMessageTypes.length > 0 && (
              <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-xs">
                <Hash className="w-3 h-3 text-gray-400" />
                <span className="text-gray-500 dark:text-gray-400 text-[11px]">Type:</span>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="bg-transparent border-0 text-xs font-semibold text-gray-800 dark:text-gray-200 focus:ring-0 cursor-pointer p-0"
                >
                  <option value="all" className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">All Types</option>
                  {detectedMessageTypes.map((t) => (
                    <option key={t} value={String(t)} className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">
                      Type {t}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Page Size Select */}
            <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-xs">
              <span className="text-gray-500 dark:text-gray-400 text-[11px]">Show:</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="bg-transparent border-0 text-xs font-semibold text-gray-800 dark:text-gray-200 focus:ring-0 cursor-pointer p-0"
              >
                <option value={25} className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">25</option>
                <option value={50} className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">50</option>
                <option value={100} className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">100</option>
                <option value={200} className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">200</option>
              </select>
            </div>
          </div>
        </div>

        {/* Detected ORA Error Code Quick Filter Pills */}
        {detectedOraCodes.length > 0 && (
          <div className="pt-2 border-t border-gray-100 dark:border-gray-800 flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 flex items-center gap-1 mr-1">
              <ShieldAlert className="w-3 h-3 text-rose-500" />
              Detected Errors:
            </span>
            <button
              onClick={() => setSelectedOraCode('all')}
              className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors cursor-pointer ${selectedOraCode === 'all'
                  ? 'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900 font-bold'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
            >
              All Errors
            </button>
            {detectedOraCodes.map((code) => (
              <button
                key={code}
                onClick={() => setSelectedOraCode(selectedOraCode === code ? 'all' : code)}
                className={`px-2 py-0.5 rounded text-[10px] font-mono font-medium transition-colors cursor-pointer border ${selectedOraCode === code
                    ? 'bg-rose-500 text-white border-rose-600 font-bold'
                    : 'bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-900 hover:bg-rose-100 dark:hover:bg-rose-900/60'
                  }`}
              >
                {code}
              </button>
            ))}
            {(selectedSeverity !== 'all' || selectedType !== 'all' || selectedOraCode !== 'all' || searchQuery) && (
              <button
                onClick={() => {
                  setSelectedSeverity('all');
                  setSelectedType('all');
                  setSelectedOraCode('all');
                  setSearchQuery('');
                }}
                className="ml-auto text-[11px] text-orange-600 dark:text-orange-400 hover:underline cursor-pointer"
              >
                Reset All Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Main Alert Log Table / Terminal Stream */}
      <div className="bg-white dark:bg-gray-900/90 rounded-xl border border-gray-200 dark:border-gray-800/80 shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-20 flex flex-col items-center justify-center gap-3">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
            <p className="text-xs text-gray-500 dark:text-gray-400">Loading Oracle alert log stream...</p>
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="py-16 text-center">
            <Terminal className="w-10 h-10 text-gray-300 dark:text-gray-700 mx-auto mb-2" />
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">No alert log entries found</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Try adjusting your search criteria or filter options.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-gray-50/80 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700/80 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  <th className="py-2.5 px-3.5 w-44">Timestamp</th>
                  <th className="py-2.5 px-3 w-28">Severity</th>
                  <th className="py-2.5 px-3 w-20">Type</th>
                  <th className="py-2.5 px-3">Message Text</th>
                  <th className="py-2.5 px-3 w-36">Host & Target</th>
                  <th className="py-2.5 px-3 w-24 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800/60 font-mono text-[11px]">
                {paginatedEntries.map((entry, idx) => {
                  const globalIdx = (currentPage - 1) * pageSize + idx;
                  const isExpanded = !!expandedRows[globalIdx];
                  const isCopied = copiedIndex === globalIdx;
                  const isError = (entry.severity || '').toLowerCase() === 'error';
                  const hasMultipleLines = entry.message_text && entry.message_text.includes('\n');
                  const firstLine = hasMultipleLines ? entry.message_text.split('\n')[0] : entry.message_text;

                  return (
                    <tr
                      key={globalIdx}
                      className={`hover:bg-gray-50/70 dark:hover:bg-gray-800/40 transition-colors ${isError ? 'bg-rose-500/[0.02] dark:bg-rose-500/[0.04]' : ''
                        }`}
                    >
                      {/* Timestamp */}
                      <td className="py-2.5 px-3.5 whitespace-nowrap text-gray-600 dark:text-gray-300 font-sans">
                        <div className="font-semibold text-gray-900 dark:text-gray-100 text-xs">
                          {formatDate(entry.timestamp)}
                        </div>
                        <div className="text-[10px] text-gray-400">
                          {formatTimeAgo(entry.timestamp)}
                        </div>
                      </td>

                      {/* Severity */}
                      <td className="py-2.5 px-3 whitespace-nowrap font-sans">
                        {renderSeverityBadge(entry.severity)}
                      </td>

                      {/* Message Type */}
                      <td className="py-2.5 px-3 whitespace-nowrap">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 font-mono">
                          T#{entry.message_type ?? '—'}
                        </span>
                      </td>

                      {/* Message Text */}
                      <td className="py-2.5 px-3 max-w-xl text-gray-800 dark:text-gray-200">
                        {isExpanded ? (
                          <div className="space-y-1.5">
                            <pre className="whitespace-pre-wrap font-mono text-[11px] leading-relaxed p-2.5 rounded-lg bg-gray-900 text-gray-100 dark:bg-black/80 dark:text-gray-100 border border-gray-700 overflow-x-auto selection:bg-orange-500 selection:text-white">
                              {renderFormattedMessageText(entry.message_text)}
                            </pre>
                            <button
                              onClick={() => setExpandedRows((prev) => ({ ...prev, [globalIdx]: false }))}
                              className="text-[10px] text-orange-600 dark:text-orange-400 hover:underline flex items-center gap-1 font-sans cursor-pointer"
                            >
                              <ChevronUp className="w-3 h-3" /> Show Less
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <p className="line-clamp-2 leading-relaxed">
                              {renderFormattedMessageText(firstLine)}
                            </p>
                            {hasMultipleLines && (
                              <button
                                onClick={() => setExpandedRows((prev) => ({ ...prev, [globalIdx]: true }))}
                                className="text-[10px] text-orange-600 dark:text-orange-400 hover:underline flex items-center gap-1 font-sans cursor-pointer font-medium"
                              >
                                <ChevronDown className="w-3 h-3" /> Full Trace ({entry.message_text.split('\n').length} lines)
                              </button>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Host & Target */}
                      <td className="py-2.5 px-3 whitespace-nowrap text-gray-500 dark:text-gray-400 font-sans">
                        <div className="font-medium text-gray-700 dark:text-gray-300 text-xs">
                          {entry.hostname || '—'}
                        </div>
                        <div className="text-[10px] text-gray-400 font-mono">
                          {entry.ip ? `${entry.ip}` : entry.config_id || '—'}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="py-2.5 px-3 whitespace-nowrap text-right font-sans">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* Copy message */}
                          <button
                            onClick={() => handleCopyMessage(entry.message_text, globalIdx)}
                            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors cursor-pointer"
                            title="Copy message"
                          >
                            {isCopied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>

                          {/* View details */}
                          <button
                            onClick={() => setSelectedEntry(entry)}
                            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-orange-600 dark:text-orange-400 transition-colors cursor-pointer"
                            title="View log details"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Bar */}
        {!loading && filteredEntries.length > 0 && (
          <div className="py-3 px-4 bg-gray-50/80 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700/80 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-600 dark:text-gray-400">
            <div>
              Showing <span className="font-semibold text-gray-900 dark:text-white">{(currentPage - 1) * pageSize + 1}</span> to{' '}
              <span className="font-semibold text-gray-900 dark:text-white">
                {Math.min(currentPage * pageSize, totalEntries)}
              </span>{' '}
              of <span className="font-semibold text-gray-900 dark:text-white">{totalEntries.toLocaleString()}</span> entries
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-2 py-1 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer text-xs"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-2 text-xs font-medium text-gray-800 dark:text-gray-200">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-1 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2 py-1 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer text-xs"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Slide-over Drawer for Log Details */}
      {selectedEntry &&
        createPortal(
          <div className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="w-full max-w-2xl bg-white dark:bg-gray-900 h-full shadow-2xl flex flex-col border-l border-gray-200 dark:border-gray-800 overflow-hidden">
              {/* Drawer Header */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800/40">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-lg bg-orange-500/10 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                      Alert Log Details
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatDate(selectedEntry.timestamp)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedEntry(null)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Drawer Content */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Status Badges Row */}
                <div className="flex flex-wrap items-center gap-2">
                  {renderSeverityBadge(selectedEntry.severity)}
                  <span className="px-2 py-0.5 rounded text-xs font-mono font-semibold bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
                    Message Type: {selectedEntry.message_type ?? 'N/A'}
                  </span>
                  <span className="px-2 py-0.5 rounded text-xs font-semibold bg-orange-100 dark:bg-orange-950/60 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800">
                    DB: Oracle
                  </span>
                </div>

                {/* Full Message Text */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      Message Content & Stack Trace
                    </label>
                    <button
                      onClick={() => handleCopyMessage(selectedEntry.message_text, -1)}
                      className="text-xs text-orange-600 dark:text-orange-400 hover:underline flex items-center gap-1 cursor-pointer"
                    >
                      <Copy className="w-3 h-3" /> Copy Message
                    </button>
                  </div>
                  <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed p-3.5 rounded-xl bg-gray-950 text-gray-100 dark:bg-black border border-gray-800 overflow-x-auto shadow-inner selection:bg-orange-500 selection:text-white">
                    {renderFormattedMessageText(selectedEntry.message_text)}
                  </pre>
                </div>

                {/* Key Metadata Table */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Execution & Origin Context
                  </label>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60">
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider block">
                        Timestamp (UTC)
                      </span>
                      <span className="font-mono font-medium text-gray-900 dark:text-white">
                        {selectedEntry.timestamp}
                      </span>
                    </div>

                    <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60">
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider block">
                        Log Timestamp
                      </span>
                      <span className="font-mono font-medium text-gray-900 dark:text-white">
                        {selectedEntry.log_timestamp || '—'}
                      </span>
                    </div>

                    <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60">
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider block">
                        Hostname
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {selectedEntry.hostname || '—'}
                      </span>
                    </div>

                    <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60">
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider block">
                        IP Address
                      </span>
                      <span className="font-mono font-medium text-gray-900 dark:text-white">
                        {selectedEntry.ip || '—'}
                      </span>
                    </div>

                    <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60">
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider block">
                        Config ID / Connection
                      </span>
                      <span className="font-mono font-medium text-gray-900 dark:text-white truncate block">
                        {selectedEntry.config_id || '—'}
                      </span>
                    </div>

                    <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60">
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider block">
                        Operating System
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white uppercase">
                        {selectedEntry.os || '—'}
                      </span>
                    </div>

                    <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60">
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider block">
                        Cluster ID
                      </span>
                      <span className="font-mono text-gray-900 dark:text-white truncate block">
                        {selectedEntry.cluster_id || '—'}
                      </span>
                    </div>

                    <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60">
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider block">
                        Indexed At
                      </span>
                      <span className="font-mono text-gray-900 dark:text-white truncate block">
                        {selectedEntry.indexed_at || '—'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Raw JSON viewer */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      Raw JSON Record
                    </label>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(JSON.stringify(selectedEntry, null, 2));
                        showSuccess('JSON copied to clipboard');
                      }}
                      className="text-xs text-orange-600 dark:text-orange-400 hover:underline flex items-center gap-1 cursor-pointer"
                    >
                      <Copy className="w-3 h-3" /> Copy JSON
                    </button>
                  </div>
                  <pre className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 text-[11px] font-mono text-gray-800 dark:text-gray-200 max-h-56 overflow-y-auto">
                    {JSON.stringify(selectedEntry, null, 2)}
                  </pre>
                </div>
              </div>

              {/* Drawer Footer */}
              <div className="p-3.5 border-t border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/40 flex justify-end">
                <button
                  onClick={() => setSelectedEntry(null)}
                  className="px-4 py-1.5 text-xs font-medium rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors cursor-pointer"
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default OracleAlertLogView;
